import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import { Calendar, Clock, ArrowRight } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { dataCache } from "@/lib/dataCache";

const Blog = () => {
  const navigate = useNavigate();
  
  const schema = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "HiStage Blog",
    "description": "Tips, stories, and insights for creators and fans on the HiStage platform",
    "url": "https://histage.lovable.app/blog"
  };

  const { data: posts = [], isLoading } = useQuery({
    queryKey: ["blog-posts"],
    queryFn: async () => {
      // Check cache first
      const cached = dataCache.get("blog-posts");
      if (cached) return cached;

      const { data, error } = await supabase
        .from("blog_posts")
        .select(`
          *,
          profiles:author_id (
            display_name,
            username
          )
        `)
        .eq("is_published", true)
        .order("published_at", { ascending: false });

      if (error) throw error;
      
      // Cache for 5 minutes
      if (data) {
        dataCache.set("blog-posts", data, 5 * 60 * 1000);
      }
      return data || [];
    },
    staleTime: 5 * 60 * 1000,
  });

  const blogPosts = Array.isArray(posts) ? posts : [];

  return (
    <>
      <SEO 
        title="HiStage Blog - Creator Tips, Stories & Insights"
        description="Explore expert tips, success stories, and industry insights for live streaming creators and engaged fans on HiStage. Learn how to grow your audience and maximize engagement."
        url="/blog"
        keywords={[
          "live streaming blog",
          "creator tips",
          "streaming advice",
          "fan engagement strategies",
          "monetization tips",
          "live streaming news"
        ]}
        schema={schema}
      />
      
      <main className="min-h-screen bg-background">
        {/* Hero */}
        <section className="py-20 bg-gradient-to-b from-primary/5 to-background">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-5xl font-bold text-foreground mb-6">
                HiStage Blog
              </h1>
              <p className="text-xl text-muted-foreground">
                Tips, stories, and insights for creators and fans
              </p>
            </div>
          </div>
        </section>

        {/* Blog Posts Grid */}
        <section className="py-20">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              {isLoading ? (
                <div className="flex justify-center py-12">
                  <LoadingSpinner />
                </div>
              ) : blogPosts.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-muted-foreground">No blog posts yet. Check back soon!</p>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {blogPosts.map((post) => (
                    <Card
                      key={post.id} 
                      className="glass-card p-6 rounded-xl border border-border/50 hover:border-primary/50 transition-all cursor-pointer overflow-hidden"
                      onClick={() => navigate(`/blog/${post.slug}`)}
                    >
                      {post.featured_image && (
                        <div className="mb-4 -mx-6 -mt-6">
                          <img 
                            src={post.featured_image} 
                            alt={post.title}
                            loading="lazy"
                            className="w-full h-48 object-cover"
                          />
                        </div>
                      )}
                      <div className="mb-3">
                        <span className="text-xs font-semibold text-primary uppercase tracking-wide">
                          {post.category}
                        </span>
                      </div>
                      <h2 className="text-2xl font-bold text-foreground mb-3 hover:text-primary transition-colors">
                        {post.title}
                      </h2>
                      <p className="text-muted-foreground mb-4 line-clamp-3">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                        <time dateTime={post.published_at} className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          <span>
                            {new Date(post.published_at).toLocaleDateString('en-US', { 
                              month: 'short', 
                              day: 'numeric', 
                              year: 'numeric' 
                            })}
                          </span>
                        </time>
                        <div className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          <span>{post.read_time}</span>
                        </div>
                      </div>
                      <Button variant="ghost" className="group">
                        Read More
                        <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                      </Button>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Newsletter CTA */}
        <section className="py-20 bg-gradient-to-b from-background to-primary/5">
          <div className="container mx-auto px-6">
            <div className="clean-card rounded-2xl p-12 max-w-3xl mx-auto text-center">
              <h2 className="text-3xl font-bold text-foreground mb-4">
                Stay Updated
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                Get the latest tips, stories, and platform updates delivered to your inbox
              </p>
              <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
                <input 
                  type="email" 
                  placeholder="Enter your email" 
                  className="flex-1 px-4 py-3 rounded-full border border-input bg-background text-foreground"
                />
                <Button size="lg" className="rounded-full px-8">
                  Subscribe
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default Blog;
